import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSwellMaps, BASIN_ORDER } from '../data/swellMaps';
import { useBasinArchive, useMapWindowRender } from '../data/swellArchive';
import SwellWindow from '../components/SwellWindow';
import { useForecastSpots, DEFAULT_SPOTS, loadSpotForecast, loadSpotForecastCached } from '../data/forecastSpots';
import {
  mToFt,
  kmhToKn,
  sampleAt,
  swellColor,
  windColor,
  dirLabel,
  fmtFt,
  fmtKn,
  fmtPeriod,
  arrowRotation,
  tideStateArrow,
  tideStateLabel,
} from '../utils/conditions';
import { loadWorldPacificBathyUrl, basinCropStyle } from '../utils/pacificBathy';

const SPEEDS = [0.25, 0.5, 1, 2, 4, 8];
const SPEED_MIN = 0.1;
const SPEED_MAX = 16;
const HIDE_DESKTOP = 7000; // idle time before the overlay controls fade (desktop)
const HIDE_TOUCH = 3500; //   …shorter on phones, where the map is the whole screen
const HIDE_CONFIG = 1400; // after picking a setting (basin/period/speed) get out of the way fast

const PT_ZONE = 'America/Los_Angeles';
function frameTimeLabel(sec) {
  const d = new Date(sec * 1000);
  const day = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: PT_ZONE });
  const hr = d.toLocaleTimeString([], { hour: 'numeric', timeZone: PT_ZONE });
  const tz = new Intl.DateTimeFormat('en-US', { timeZone: PT_ZONE, timeZoneName: 'short' })
    .formatToParts(d).find((p) => p.type === 'timeZoneName')?.value || 'PT';
  return `${day} · ${hr} ${tz}`;
}

// Project a spot's lat/lng to a fractional position within a basin's rendered
// frame, using the same linear mapping the backend renderer uses (longitude is
// wrapped into the basin's continuous lon window). Returns null when the spot
// falls outside the basin.
function projectSpot(spot, doc) {
  const { latMin, latMax, lonMin, lonMax } = doc || {};
  if (![latMin, latMax, lonMin, lonMax].every(Number.isFinite)) return null;
  if (!Number.isFinite(spot.lat) || !Number.isFinite(spot.lng)) return null;
  if (spot.lat < latMin || spot.lat > latMax) return null;
  let clon = spot.lng;
  while (clon < lonMin) clon += 360;
  while (clon > lonMax) clon -= 360;
  if (clon < lonMin || clon > lonMax) return null;
  return {
    xFrac: (clon - lonMin) / (lonMax - lonMin || 1),
    yFrac: (latMax - spot.lat) / (latMax - latMin || 1),
  };
}

// Present-conditions content for a spot's hover/click card.
function SpotConditionsCard({ spot, entry, onRetry }) {
  const cond = entry && !entry.loading && !entry.error ? entry.cond : null;
  const sample = cond
    ? sampleAt(cond, Date.now()) ||
      (cond.hours?.length ? cond.hours.find((h) => h.t * 1000 >= Date.now()) || cond.hours[0] : null)
    : null;
  const swellFt = sample ? mToFt(sample.swellH != null ? sample.swellH : sample.waveH) : null;
  const windKn = sample ? kmhToKn(sample.windSpd) : null;
  const tideFt = sample ? mToFt(sample.tideM) : null;
  const period = sample ? (sample.swellPeak != null ? sample.swellPeak : sample.swellP) : null;
  return (
    <>
      <div className="cs-sm-sc-name">{spot.name}</div>
      {spot.region && <div className="cs-sm-sc-region">{spot.region}</div>}
      {!entry || entry.loading ? (
        <div className="cs-sm-sc-msg">Loading conditions…</div>
      ) : entry.error ? (
        <button type="button" className="cs-sm-sc-retry" onClick={(e) => { e.stopPropagation(); onRetry?.(); }}>
          Couldn’t load — tap to retry
        </button>
      ) : !sample ? (
        <div className="cs-sm-sc-msg">Conditions unavailable</div>
      ) : (
        <div className="cs-sm-sc-metrics">
          <div className="cs-sm-sc-metric">
            <span className="cs-sm-sc-lbl">Swell</span>
            <span className="cs-sm-sc-val" style={{ color: swellColor(swellFt) }}>
              {fmtFt(swellFt)}
              {period != null && <em> @ {fmtPeriod(period)}</em>}
            </span>
            {sample.swellDir != null && (
              <span className="cs-sm-sc-dir">
                <span className="cs-sm-sc-arrow" style={{ transform: `rotate(${arrowRotation(sample.swellDir)}deg)` }}>↑</span>
                {dirLabel(sample.swellDir)}
              </span>
            )}
          </div>
          <div className="cs-sm-sc-metric">
            <span className="cs-sm-sc-lbl">Wind</span>
            <span className="cs-sm-sc-val" style={{ color: windColor(windKn) }}>{fmtKn(windKn)}</span>
            {sample.windDir != null && (
              <span className="cs-sm-sc-dir">
                <span className="cs-sm-sc-arrow" style={{ transform: `rotate(${arrowRotation(sample.windDir)}deg)` }}>↑</span>
                {dirLabel(sample.windDir, false)}
              </span>
            )}
          </div>
          <div className="cs-sm-sc-metric">
            <span className="cs-sm-sc-lbl">Tide</span>
            <span className="cs-sm-sc-val">{tideFt == null ? '—' : `${tideFt.toFixed(1)} ft`}</span>
            {sample.tideState && (
              <span className="cs-sm-sc-dir">{tideStateArrow(sample.tideState)} {tideStateLabel(sample.tideState)}</span>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default function SwellMap() {
  const { basins, loading } = useSwellMaps();

  const present = useMemo(() => BASIN_ORDER.filter((id) => basins[id]), [basins]);
  const [basinId, setBasinId] = useState(null);
  useEffect(() => {
    if ((!basinId || !basins[basinId]) && present.length) setBasinId(present[0]);
  }, [present, basinId, basins]);

  const liveDoc = basinId ? basins[basinId] : null;

  // --- Live / History window. In History mode we request an on-demand render of
  //     the chosen past ~86-day window from the archive and play that instead. ---
  const { manifest } = useBasinArchive(basinId);
  const extent = manifest ? { minTime: manifest.minTime, maxTime: manifest.maxTime } : null;
  const [win, setWin] = useState({ mode: 'live' });
  const isHistory = win.mode === 'history';
  const render = useMapWindowRender(basinId, win.startSec, win.endSec, isHistory);

  // Synthesize a doc-shaped view from the history render result so the rest of
  // the player (scrubber, spots, labels) works unchanged.
  const histDoc = useMemo(() => {
    if (!isHistory || render.status !== 'ready' || !render.variants) return null;
    const base = liveDoc?.variants?.length ? liveDoc.variants : [{ key: 'all', label: 'All' }];
    const variants = base
      .filter((v) => render.variants[v.key])
      .map((v) => ({ ...v, url: render.variants[v.key] }));
    return {
      ...liveDoc,
      variants,
      posterUrl: render.posterUrl,
      windVideo: render.windVideo,
      windPosterUrl: render.windPosterUrl,
      cloudVideo: render.cloudVideo,
      cloudPosterUrl: render.cloudPosterUrl,
      rainVideo: render.rainVideo,
      rainPosterUrl: render.rainPosterUrl,
      startTime: render.startTimeActual || render.startTime,
      endTime: render.endTimeActual || render.endTime,
      width: render.width,
      height: render.height,
      nowFrac: 1, // pure past window — "now" sits at the far (latest) edge
    };
  }, [isHistory, render, liveDoc]);

  const doc = isHistory ? histDoc : liveDoc;
  const variants = useMemo(() => doc?.variants || [], [doc]);
  const [variantKey, setVariantKey] = useState('all');
  useEffect(() => {
    if (variants.length && !variants.some((v) => v.key === variantKey)) setVariantKey(variants[0].key);
  }, [variants, variantKey]);
  const cur = variants.find((v) => v.key === variantKey) || variants[0];

  // --- Layers. Any combination can be shown at once: stills (sat/bathy) sit at
  //     the bottom of the stack, layer MP4s stack above with additive (screen)
  //     blending + a per-layer opacity slider, all time-synced to one master
  //     video. The bakes are all ocean-masked (land stays a flat silhouette in
  //     every layer), so compositing never paints data over continents. ---
  const hasWind = !!doc?.windVideo;
  const hasCloud = !!doc?.cloudVideo;
  const hasRain = !!doc?.rainVideo;
  // Live sat mosaic lives on swellMaps/world (Pacific window); reuse for all basins.
  const worldSat = basins?.world || null;
  const satUrl = worldSat?.satUrl || liveDoc?.satUrl || '';
  const satAt = worldSat?.satAt || liveDoc?.satAt || null;
  const hasSat = !!satUrl && !isHistory;
  const [bathyUrl, setBathyUrl] = useState('');
  useEffect(() => {
    let cancelled = false;
    loadWorldPacificBathyUrl()
      .then((u) => { if (!cancelled && u) setBathyUrl(u); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const hasBathy = !!bathyUrl;
  // Crop Pacific-window stills (sat / bathy) into the active basin lon/lat box.
  const stillCropStyle = useMemo(() => basinCropStyle(liveDoc), [liveDoc]);
  // Per-layer on/off + opacity. Defaults: swell only, full strength.
  const [layerCfg, setLayerCfg] = useState({
    bathy: { on: false, op: 1 },
    sat: { on: false, op: 1 },
    swell: { on: true, op: 1 },
    cloud: { on: false, op: 0.55 },
    rain: { on: false, op: 0.7 },
    wind: { on: false, op: 0.8 },
  });
  const [opOpen, setOpOpen] = useState(false);
  const setLayerOp = (key, op) => setLayerCfg((c) => ({ ...c, [key]: { ...c[key], op } }));
  // Stack order bottom→top: imagery bases, then swell, then atmosphere, wind on top.
  const layerDefs = useMemo(() => [
    { key: 'bathy', label: 'Bathy', dot: 'bathy', kind: 'still', avail: hasBathy, src: bathyUrl },
    { key: 'sat', label: 'Live sat', dot: 'sat', kind: 'still', avail: hasSat, src: satUrl },
    { key: 'swell', label: 'Swell', dot: 'swell', kind: 'video', avail: !!cur?.url, url: cur?.url, poster: doc?.posterUrl },
    { key: 'cloud', label: 'Cloud', dot: 'cloud', kind: 'video', avail: hasCloud, url: doc?.cloudVideo, poster: doc?.cloudPosterUrl },
    { key: 'rain', label: 'Rain', dot: 'rain', kind: 'video', avail: hasRain, url: doc?.rainVideo, poster: doc?.rainPosterUrl },
    { key: 'wind', label: 'Wind', dot: 'wind', kind: 'video', avail: hasWind, url: doc?.windVideo, poster: doc?.windPosterUrl },
  ], [hasBathy, bathyUrl, hasSat, satUrl, cur, doc, hasCloud, hasRain, hasWind]);
  const activeLayers = useMemo(
    () => layerDefs.filter((l) => layerCfg[l.key].on && l.avail),
    [layerDefs, layerCfg]
  );
  const videoLayers = useMemo(() => activeLayers.filter((l) => l.kind === 'video'), [activeLayers]);
  const anyVideo = videoLayers.length > 0;
  // The bottom-most active video drives the timeline; the rest follow it.
  const masterKey = videoLayers[0]?.key || null;
  const toggleLayer = (key) => {
    setLayerCfg((c) => {
      const on = !c[key].on;
      // Never let the stage go empty — turning off the last visible layer is a no-op.
      if (!on && !layerDefs.some((l) => l.key !== key && c[l.key].on && l.avail)) return c;
      return { ...c, [key]: { ...c[key], on } };
    });
  };
  // Chip display order (most-used first) — independent of stack order.
  const chipOrder = ['swell', 'wind', 'cloud', 'rain', 'sat', 'bathy'];
  const layerLabel = activeLayers.length
    ? activeLayers.map((l) => l.label).join(' + ')
    : 'Swell energy';
  const layerDot = (videoLayers[0] || activeLayers[0])?.dot || 'swell';

  const [speed, setSpeed] = useState(1);
  const setSpeedClamped = (v) => setSpeed(Math.min(SPEED_MAX, Math.max(SPEED_MIN, Number(v) || 1)));
  const [playing, setPlaying] = useState(true);
  const [frac, setFrac] = useState(0);

  const videoRefs = useRef({}); // layer key -> <video> element
  const stageRef = useRef(null);
  const posRef = useRef(0); // last playback fraction, preserved across variant swaps

  // --- Mobile: maximize the map. In landscape on a phone we reclaim the navbar
  //     (CSS pseudo-fullscreen, reliable on iOS where a <div> can't go truly
  //     fullscreen) and best-effort request the real Fullscreen API on the first
  //     touch (Android), which also hides the browser chrome. ---
  const isTouch = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(hover: none) and (pointer: coarse)').matches,
    []
  );
  const [landscape, setLandscape] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(orientation: landscape)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(orientation: landscape)');
    const on = () => setLandscape(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on));
  }, []);
  const fsMode = isTouch && landscape;
  const fsTried = useRef(false);
  useEffect(() => {
    if (!fsMode) {
      fsTried.current = false;
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    }
  }, [fsMode]);
  const tryFullscreen = useCallback(() => {
    if (!fsMode || fsTried.current || document.fullscreenElement) return;
    fsTried.current = true;
    stageRef.current?.requestFullscreen?.().catch(() => {});
  }, [fsMode]);

  const nowFrac = doc?.nowFrac || 0;
  const startTime = doc?.startTime;
  const endTime = doc?.endTime;

  // --- Tracked surf spots overlaid as interactive dots over the video ---
  const fsSpots = useForecastSpots();
  // Merge the shipped defaults with the live Firestore list (Firestore wins on
  // id) so newly added spots show immediately even before they're seeded.
  const spots = useMemo(() => {
    const map = new Map();
    DEFAULT_SPOTS.forEach((s) => map.set(s.id, s));
    (fsSpots || []).forEach((s) => map.set(s.id, { ...map.get(s.id), ...s }));
    return Array.from(map.values()).filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lng));
  }, [fsSpots]);

  // Track the rendered stage size so dots align with the letterboxed (object-fit:
  // contain) video rect, whatever the viewport/orientation.
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => setStageSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Every layer bake stamps its date bar + legend in the top-left corner of the
  // frame. When videos are stacked, hide that corner on the overlay videos (a
  // CSS mask over the letterbox-adjusted content box) so exactly one HUD — the
  // master layer's — stays readable. Date stamps are identical across synced
  // layers anyway; it's the differing legends that would double-print.
  const hudMaskStyle = useMemo(() => {
    const VW = doc?.width;
    const VH = doc?.height;
    if (!Number.isFinite(VW) || !Number.isFinite(VH) || !stageSize.w || !stageSize.h) return null;
    const scale = Math.min(stageSize.w / VW, stageSize.h / VH);
    const offX = (stageSize.w - VW * scale) / 2;
    const offY = (stageSize.h - VH * scale) / 2;
    const x = Math.round(offX + VW * scale * 0.165);
    const y = Math.round(offY + VH * scale * 0.16);
    const masks = `linear-gradient(to right, transparent ${x}px, #000 ${x + 16}px), linear-gradient(to bottom, transparent ${y}px, #000 ${y + 16}px)`;
    return {
      WebkitMaskImage: masks,
      WebkitMaskComposite: 'source-over',
      maskImage: masks,
      maskComposite: 'add',
    };
  }, [doc, stageSize]);

  const placedSpots = useMemo(() => {
    if (!doc || !stageSize.w || !stageSize.h) return [];
    const VW = doc.width;
    const VH = doc.height;
    if (!Number.isFinite(VW) || !Number.isFinite(VH)) return [];
    // Replicate object-fit: contain to map frame fractions → stage pixels.
    const scale = Math.min(stageSize.w / VW, stageSize.h / VH);
    const dispW = VW * scale;
    const dispH = VH * scale;
    const offX = (stageSize.w - dispW) / 2;
    const offY = (stageSize.h - dispH) / 2;
    const out = [];
    for (const spot of spots) {
      const p = projectSpot(spot, doc);
      if (!p) continue;
      out.push({
        spot,
        x: offX + p.xFrac * dispW,
        y: offY + p.yFrac * dispH,
        xFrac: p.xFrac,
        yFrac: p.yFrac,
      });
    }
    return out;
  }, [doc, stageSize, spots]);

  // Active spot whose conditions card is shown: hover (transient) or click (pinned).
  const [active, setActive] = useState(null); // { id, pinned }
  const [conds, setConds] = useState({}); // id -> { loading?, cond?, error? }

  // Background-prefetch the *cached* conditions for every visible spot (cheap
  // Firestore reads, no live API fan-out) so tapping a dot is instant. A ref
  // dedupes so we read each spot's cache at most once per session.
  const prefetchedRef = useRef(new Set());
  useEffect(() => {
    if (!placedSpots.length) return undefined;
    const queue = placedSpots.map((p) => p.spot).filter((s) => !prefetchedRef.current.has(s.id));
    if (!queue.length) return undefined;
    let cancelled = false;
    let idx = 0;
    const worker = async () => {
      while (!cancelled && idx < queue.length) {
        const spot = queue[idx];
        idx += 1;
        prefetchedRef.current.add(spot.id);
        // eslint-disable-next-line no-await-in-loop
        const cond = await loadSpotForecastCached(spot);
        if (cancelled) return;
        if (cond) setConds((c) => (c[spot.id]?.cond ? c : { ...c, [spot.id]: { loading: false, cond } }));
      }
    };
    // Start a touch after the video begins loading so we don't compete with it.
    const t = setTimeout(() => { worker(); worker(); }, 900);
    return () => { cancelled = true; clearTimeout(t); };
  }, [placedSpots]);

  // On hover/tap, ensure we have the spot's conditions. Cached spots are already
  // populated by the prefetch above; otherwise fall back to a live fetch, capped
  // with a timeout so a slow/down provider can't leave the card spinning forever.
  useEffect(() => {
    const id = active?.id;
    if (!id) return undefined;
    const entry = conds[id];
    if (entry && (entry.cond || entry.loading)) return undefined;
    const spot = spots.find((s) => s.id === id);
    if (!spot) return undefined;
    let cancelled = false;
    setConds((c) => ({ ...c, [id]: { loading: true } }));
    const timeout = new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 12000));
    Promise.race([loadSpotForecast(spot, 2), timeout])
      .then((cond) => { if (!cancelled) setConds((c) => ({ ...c, [id]: { loading: false, cond } })); })
      .catch(() => { if (!cancelled) setConds((c) => ({ ...c, [id]: { loading: false, error: true } })); });
    return () => { cancelled = true; };
  }, [active, conds, spots]);

  // Retry a failed conditions load (clears the error so the effect refetches).
  const retrySpot = (id) => setConds((c) => { const n = { ...c }; delete n[id]; return n; });

  const activePlaced = active ? placedSpots.find((p) => p.spot.id === active.id) : null;
  const enterSpot = (id) => { if (!isTouch) setActive((a) => (a?.pinned ? a : { id, pinned: false })); };
  const leaveSpot = (id) => { if (!isTouch) setActive((a) => (a && !a.pinned && a.id === id ? null : a)); };
  const clickSpot = (e, id) => {
    e.stopPropagation();
    setActive((a) => (a?.id === id && a.pinned ? null : { id, pinned: true }));
  };

  // --- Full-screen layout: size the stage to the viewport below the navbar ---
  const [navH, setNavH] = useState(56);
  useEffect(() => {
    const measure = () => {
      const nav = document.querySelector('.cs-nav');
      if (nav) setNavH(nav.offsetHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // --- Auto-hiding overlay controls (idle → show only the map) ---
  const [uiVisible, setUiVisible] = useState(true);
  const hideTimer = useRef(null);
  const hoverRef = useRef(false); // pointer over a control → never hide
  const idleHide = isTouch ? HIDE_TOUCH : HIDE_DESKTOP;
  const poke = useCallback((delay) => {
    setUiVisible(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      if (!hoverRef.current) setUiVisible(false);
    }, delay || idleHide);
  }, [idleHide]);
  // After choosing a setting, get the controls out of the way quickly (esp. mobile).
  const configPoke = useCallback(() => poke(isTouch ? HIDE_CONFIG : 3000), [poke, isTouch]);
  useEffect(() => {
    poke();
    return () => hideTimer.current && clearTimeout(hideTimer.current);
  }, [poke]);

  // Seed the playhead at "now" once per basin — including the FIRST load, where
  // the Firestore doc (and its nowFrac) arrives after mount. Without this the
  // map used to start playing from the far-history end of the timeline.
  const seededRef = useRef(null);
  useEffect(() => {
    if (!doc || isHistory || seededRef.current === basinId) return;
    seededRef.current = basinId;
    posRef.current = nowFrac;
    setFrac(nowFrac);
    for (const v of Object.values(videoRefs.current)) {
      if (v && v.duration) v.currentTime = Math.min(v.duration - 0.05, Math.max(0, nowFrac * v.duration));
    }
  }, [doc, isHistory, basinId, nowFrac]);

  const eachVideo = (fn) => {
    for (const l of videoLayers) {
      const v = videoRefs.current[l.key];
      if (v) fn(v, l.key);
    }
  };

  // A layer video finished (re)loading: restore the shared playhead and speed.
  // Runs for master swaps (variant/basin/history) AND for overlay layers the
  // user just toggled on, so every video joins the stack in sync.
  const onLayerMeta = (e) => {
    const v = e.currentTarget;
    if (v.duration && Number.isFinite(v.duration)) {
      v.currentTime = Math.min(v.duration - 0.05, Math.max(0, posRef.current * v.duration));
    }
    v.playbackRate = speed;
    if (playing) v.play().catch(() => {});
    else v.pause();
  };

  useEffect(() => {
    eachVideo((v) => { v.playbackRate = speed; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speed]);

  const togglePlay = () => {
    setActive(null);
    const m = masterKey ? videoRefs.current[masterKey] : null;
    if (!m) return;
    const next = m.paused;
    eachVideo((v) => {
      if (next) v.play().catch(() => {});
      else v.pause();
    });
    setPlaying(next);
  };

  // Master timeupdate drives the scrubber AND keeps overlay videos in lockstep
  // (same nframes/fps per basin, so equal fractions are equal wall-clock times).
  const onTimeUpdate = () => {
    const m = masterKey ? videoRefs.current[masterKey] : null;
    if (!m || !m.duration) return;
    const f = m.currentTime / m.duration;
    posRef.current = f;
    setFrac(f);
    eachVideo((v, key) => {
      if (key === masterKey || !v.duration) return;
      const target = f * v.duration;
      if (Math.abs(v.currentTime - target) > 0.25) v.currentTime = target;
      if (v.playbackRate !== m.playbackRate) v.playbackRate = m.playbackRate;
      if (v.paused !== m.paused) (m.paused ? v.pause() : v.play().catch(() => {}));
    });
  };

  const seekFrac = (f) => {
    posRef.current = f;
    setFrac(f);
    eachVideo((v) => {
      if (v.duration) v.currentTime = Math.min(v.duration - 0.05, Math.max(0, f * v.duration));
    });
  };

  const curSec = startTime && endTime ? startTime + frac * (endTime - startTime) : null;
  const isForecast = frac > nowFrac + 1e-4;

  const rangeLabel = useMemo(() => {
    if (!startTime || !endTime) return '';
    const fmt = (s) => new Date(s * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
    const days = Math.round((endTime - startTime) / 86400);
    return `${fmt(startTime)} → ${fmt(endTime)} · ${days} days`;
  }, [startTime, endTime]);

  // Pin the overlay open only while a *real* pointer hovers a control. On touch
  // there's a synthetic mouseenter but no reliable mouseleave, which would leave
  // hoverRef stuck true and the controls never auto-hiding — so skip it there.
  const onPanelEnter = () => { if (isTouch) return; hoverRef.current = true; setUiVisible(true); };
  const onPanelLeave = () => { if (isTouch) return; hoverRef.current = false; poke(); };
  const hideNow = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hoverRef.current = false;
    setUiVisible(false);
  };

  return (
    <div
      ref={stageRef}
      className={`cs-sm-stage ${uiVisible ? 'ui-on' : 'ui-off'} ${isTouch ? 'sm-touch' : ''} ${fsMode ? 'sm-fs' : ''}`}
      style={{ height: fsMode ? '100dvh' : `calc(100dvh - ${navH}px)` }}
      onMouseMove={() => poke()}
      onTouchStart={() => { poke(); tryFullscreen(); }}
    >
      {loading ? (
        <div className="cs-sm-center">Loading swell maps…</div>
      ) : present.length === 0 ? (
        <div className="cs-sm-center">Swell maps are still rendering — check back in a few minutes.</div>
      ) : (
        <>
          {activeLayers.length > 0 ? (
            <div
              className="cs-sm-layers"
              onClick={anyVideo ? togglePlay : undefined}
              role={anyVideo ? 'button' : undefined}
            >
              {activeLayers.map((l, i) => {
                const isBottom = i === 0;
                const op = layerCfg[l.key].op;
                if (l.kind === 'still') {
                  return (
                    <img
                      key={l.key}
                      className={`cs-sm-video cs-sm-sat ${isBottom ? '' : 'cs-sm-blend'}`}
                      src={l.src}
                      alt={l.key === 'sat' ? 'Live satellite cloud mosaic' : 'Global ocean bathymetry'}
                      style={{ ...stillCropStyle, opacity: op }}
                    />
                  );
                }
                const isMaster = l.key === masterKey;
                return (
                  <video
                    key={l.key}
                    ref={(el) => { videoRefs.current[l.key] = el; }}
                    className={`cs-sm-video ${isBottom ? '' : 'cs-sm-blend'}`}
                    style={{
                      opacity: op,
                      ...(isMaster || !hudMaskStyle ? {} : hudMaskStyle),
                    }}
                    src={l.url}
                    poster={isMaster ? l.poster || undefined : undefined}
                    muted
                    loop
                    playsInline
                    autoPlay
                    preload="auto"
                    onLoadedMetadata={onLayerMeta}
                    onTimeUpdate={isMaster ? onTimeUpdate : undefined}
                    onPlay={isMaster ? () => setPlaying(true) : undefined}
                    onPause={isMaster ? () => setPlaying(false) : undefined}
                  />
                );
              })}
            </div>
          ) : isHistory ? (
            <div className="cs-sm-center">
              {render.status === 'error'
                ? `Couldn’t render this window: ${render.message || 'no archived data yet'}`
                : render.status === 'ready'
                ? 'No archived data for this window yet.'
                : `Rendering past window for ${basins[basinId]?.name}… reconstructing from the archive (this can take a minute the first time).`}
            </div>
          ) : (
            <div className="cs-sm-center">
              Rendering {basins[basinId]?.name} {layerLabel.toLowerCase()} video… this runs a few minutes after each refresh.
            </div>
          )}

          {/* Interactive surf-spot dots, projected over the video. Hover (desktop)
              or tap (mobile) one to see its present surf conditions. */}
          {activeLayers.length > 0 && placedSpots.length > 0 && (
            <div className="cs-sm-spots">
              {placedSpots.map(({ spot, x, y }) => (
                <button
                  key={spot.id}
                  type="button"
                  className={`cs-sm-spot ${active?.id === spot.id ? 'on' : ''}`}
                  style={{ left: `${x}px`, top: `${y}px` }}
                  onMouseEnter={() => enterSpot(spot.id)}
                  onMouseLeave={() => leaveSpot(spot.id)}
                  onClick={(e) => clickSpot(e, spot.id)}
                  aria-label={`${spot.name} surf conditions`}
                  title={spot.name}
                />
              ))}

              {activePlaced && (
                <div
                  className="cs-sm-spotcard"
                  style={{
                    left: `${activePlaced.x}px`,
                    top: `${activePlaced.y}px`,
                    transform: `translate(${
                      activePlaced.xFrac < 0.2 ? '-16px' : activePlaced.xFrac > 0.8 ? 'calc(-100% + 16px)' : '-50%'
                    }, ${activePlaced.yFrac < 0.32 ? '16px' : 'calc(-100% - 16px)'})`,
                  }}
                  onMouseEnter={() => { if (!isTouch) setActive((a) => a || { id: activePlaced.spot.id, pinned: false }); }}
                  onMouseLeave={() => leaveSpot(activePlaced.spot.id)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <SpotConditionsCard
                    spot={activePlaced.spot}
                    entry={conds[activePlaced.spot.id]}
                    onRetry={() => retrySpot(activePlaced.spot.id)}
                  />
                </div>
              )}
            </div>
          )}

          {/* Persistent tiny label (outside the fading overlay) — shows the
              current layer/basin even after the config auto-hides. */}
          <div className="cs-sm-minilabel">
            <span className={`cs-sm-dot ${layerDot}`} />
            {layerLabel}
            <span className="cs-sm-mini-basin"> · {basins[basinId]?.name}</span>
          </div>

          {/* ---- Overlay UI (auto-hides) ---- */}
          <div className="cs-sm-overlay" aria-hidden={!uiVisible}>
            {/* Manual dismiss — instant hide (tap the map to bring it back). */}
            <button className="cs-sm-hide" onClick={hideNow} aria-label="Hide controls" title="Hide controls">
              Hide ✕
            </button>

            {/* Top: compact floating config card */}
            <div className="cs-sm-top" onMouseEnter={onPanelEnter} onMouseLeave={onPanelLeave}>
              <select
                className="cs-sm-select"
                value={basinId || ''}
                onChange={(e) => { setBasinId(e.target.value); configPoke(); }}
                aria-label="Basin"
              >
                {present.map((id) => (
                  <option key={id} value={id}>{basins[id].name}</option>
                ))}
              </select>

              {/* Layer chips — multi-select toggles (any combination stacks). */}
              <span className="cs-sm-seg">
                {chipOrder.map((key) => {
                  const l = layerDefs.find((d) => d.key === key);
                  if (!l || !l.avail) return null;
                  return (
                    <button
                      key={key}
                      className={layerCfg[key].on ? 'on' : ''}
                      onClick={() => { toggleLayer(key); configPoke(); }}
                      title={`Toggle ${l.label} layer`}
                    >
                      {l.label}
                    </button>
                  );
                })}
              </span>

              {/* Per-layer opacity sliders for the active stack. */}
              <span className="cs-sm-opwrap">
                <button
                  className={`cs-sm-opbtn ${opOpen ? 'on' : ''}`}
                  onClick={() => { setOpOpen((o) => !o); poke(); }}
                  title="Layer opacity"
                  aria-expanded={opOpen}
                >
                  Opacity ▾
                </button>
                {opOpen && (
                  <div className="cs-sm-oppanel">
                    {activeLayers.slice().reverse().map((l) => (
                      <label key={l.key}>
                        <span className={`cs-sm-dot ${l.dot}`} />
                        <span className="cs-sm-oplbl">{l.label}</span>
                        <input
                          type="range"
                          min={0.15}
                          max={1}
                          step={0.05}
                          value={layerCfg[l.key].op}
                          onChange={(e) => setLayerOp(l.key, Number(e.target.value))}
                        />
                        <em>{Math.round(layerCfg[l.key].op * 100)}%</em>
                      </label>
                    ))}
                  </div>
                )}
              </span>

              {layerCfg.swell.on && variants.length > 0 && (
                <span className="cs-sm-seg">
                  {variants.map((v) => (
                    <button key={v.key} className={v.key === variantKey ? 'on' : ''} onClick={() => { setVariantKey(v.key); configPoke(); }}>
                      {v.label}
                    </button>
                  ))}
                </span>
              )}

              {anyVideo && (
                <SwellWindow
                  extent={extent}
                  value={win}
                  onChange={(w) => { setWin(w); configPoke(); }}
                  busy={isHistory && render.status !== 'ready' && render.status !== 'error'}
                />
              )}
            </div>

            {/* Bottom: playback controls (hidden when only stills are shown) */}
            {anyVideo && (
              <div className="cs-sm-bottom" onMouseEnter={onPanelEnter} onMouseLeave={onPanelLeave}>
                <div className="cs-sm-controls">
                  <button className="cs-sm-play" onClick={togglePlay} title={playing ? 'Pause' : 'Play'}>
                    {playing ? '⏸' : '▶'}
                  </button>
                  <span className="cs-sm-scrubwrap">
                    <input
                      className="cs-sm-scrub"
                      type="range"
                      min={0}
                      max={1}
                      step={0.0005}
                      value={frac}
                      onChange={(e) => seekFrac(Number(e.target.value))}
                    />
                    <span className="cs-sm-nowtick" style={{ left: `${nowFrac * 100}%` }} title="Now" />
                  </span>
                  <button
                    className={`cs-sm-now ${Math.abs(frac - nowFrac) < 0.004 ? 'on' : ''}`}
                    onClick={() => seekFrac(nowFrac)}
                    title="Jump to now"
                  >
                    Now
                  </button>
                  <span className="cs-sm-time" aria-live="off">
                    <span className="cs-sm-timetext">{curSec ? frameTimeLabel(curSec) : '\u00a0'}</span>
                    <span className={`cs-sm-tag ${curSec ? (isForecast ? 'fc' : 'hist') : 'idle'}`}>
                      {curSec ? (isForecast ? 'forecast' : 'history') : '\u00a0'}
                    </span>
                  </span>
                  <span className="cs-sm-speed" title="Timeline playback speed">
                    {SPEEDS.map((s) => (
                      <button key={s} className={Math.abs(s - speed) < 0.001 ? 'on' : ''} onClick={() => { setSpeedClamped(s); configPoke(); }}>
                        {s}×
                      </button>
                    ))}
                  </span>
                  <label className="cs-sm-speed-fine" title="Fine speed control">
                    <span>{speed < 1 ? speed.toFixed(2) : speed.toFixed(1)}×</span>
                    <input
                      type="range"
                      min={SPEED_MIN}
                      max={SPEED_MAX}
                      step={0.05}
                      value={speed}
                      onChange={(e) => { setSpeedClamped(e.target.value); configPoke(); }}
                    />
                  </label>
                </div>
                {rangeLabel && (
                  <div className="cs-sm-range">
                    {rangeLabel}
                    {layerCfg.swell.on && Number.isFinite(doc.maxPowerKw) && (
                      <span className="cs-sm-meta"> · peak energy {doc.maxPowerKw.toLocaleString()} kW/m</span>
                    )}
                    {layerCfg.swell.on && Number.isFinite(doc.maxM) && (
                      <span className="cs-sm-meta"> · peak height {mToFt(doc.maxM).toFixed(0)}ft</span>
                    )}
                    {layerCfg.wind.on && hasWind && Number.isFinite(doc.maxWindKt) && (
                      <span className="cs-sm-meta"> · peak wind {doc.maxWindKt} kt</span>
                    )}
                    {layerCfg.rain.on && hasRain && Number.isFinite(doc.maxPrecipMm) && (
                      <span className="cs-sm-meta"> · peak precip {doc.maxPrecipMm} mm</span>
                    )}
                    {layerCfg.sat.on && hasSat && satAt && (
                      <span className="cs-sm-meta"> · sat updated {new Date(satAt).toLocaleString()}</span>
                    )}
                  </div>
                )}
              </div>
            )}
            {!anyVideo && activeLayers.length > 0 && (
              <div className="cs-sm-bottom" onMouseEnter={onPanelEnter} onMouseLeave={onPanelLeave}>
                <div className="cs-sm-range">
                  {layerCfg.sat.on && hasSat ? (
                    <>
                      NASA GIBS true-color / IR mosaic (near-real-time)
                      {satAt ? <span className="cs-sm-meta"> · updated {new Date(satAt).toLocaleString()}</span> : null}
                    </>
                  ) : (
                    'Global ocean bathymetry (Pacific-centered window)'
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
